import type {
  FilterFormValues, TCandidateOrigin, TReference,
} from '@vlabs/monokit/entities/matcher/types/types';
import { formatISO } from 'date-fns/formatISO';

export const DEFAULT_SEARCH_THRESHOLD = 0.9;
export const DEFAULT_SEARCH_LIMIT = 100;
export const DEFAULT_MATCHER_LIMIT = 3;

export interface SearchCandidatePayload {
  filters: Record<string, unknown>;
  order?: string;
  limit?: number;
  targets?: string[];
  threshold?: number;
}

export interface SearchMatchPayload {
  references: TReference[];
  candidates: SearchCandidatePayload[];
}

type FormValues = FilterFormValues;

const rawValue = (values: FormValues, key: string): unknown => (
  (values as unknown as Record<string, unknown>)[key]
);

const isEmptyValue = (value: unknown): boolean => (
  value !== null && typeof value === 'object' && Object.keys(value).length === 0
);

const undefinedIfEmpty = <T>(value: T): T | undefined => (isEmptyValue(value) ? undefined : value);

const asArrayFromString = (value: unknown): unknown[] | undefined => {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value.map((item) => item as unknown);
  if (typeof value === 'string' && value.length === 0) return [];
  return String(value).split(',').map((item) => item.trim());
};

const asArrayOfInt = (value: unknown): number[] | undefined => {
  if (value === undefined) return undefined;
  const items = Array.isArray(value) ? value : [value];
  return items.map((item) => parseInt(String(item), 10));
};

const asArrayWithOptionalNull = <T>(value: T[] | undefined): (T | null)[] | undefined => (
  value?.map((item) => (item === 'null' ? null : item))
);

const asArrayOfOptionValues = (value: unknown): unknown[] => {
  if (value === undefined) return [];
  const items = Array.isArray(value) ? value : [value];
  return items
    .map((option) => (option as { value?: unknown } | null)?.value)
    .filter((item) => item !== undefined);
};

const toInt = (value: unknown): number | null | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'boolean') return Number(value);
  if (typeof value === 'string' && value === 'null') return null;
  return parseInt(String(value), 10);
};

const nanToUndefined = (value: number | null | undefined): number | null | undefined => (
  typeof value === 'number' && Number.isNaN(value) ? undefined : value
);

const at0 = (value: unknown): unknown => (value as Record<string, unknown> | undefined)?.[0];

const toISOString = (value: unknown): string | string[] | undefined => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    return value.map((item) => formatISO(item as Date));
  }
  if (value instanceof Date) return formatISO(value);
  throw new Error(`${String(value)} not known value type for iso string formatter`);
};

const floatOrUndefined = (value: unknown): number | undefined => {
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? undefined : parsed;
};

const normalizedPercentOrUndefined = (value: unknown): number | undefined => {
  const parsed = parseFloat(String(value));
  if (Number.isNaN(parsed)) return undefined;
  return Number((parsed / 100).toFixed(4));
};

const emptyStringToUndefined = (value: unknown): unknown => (
  typeof value === 'string' && value.length === 0 ? undefined : value
);

const normalizeListId = (value: unknown): string | undefined => {
  if (value === undefined) return undefined;
  const normalized = typeof value === 'object' ? (value as { value?: unknown }).value : value;
  if (normalized === undefined) return undefined;
  const stringified = String(normalized);
  return stringified.length === 0 ? undefined : stringified;
};

const normalizeLimit = (value: unknown): number => {
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? DEFAULT_MATCHER_LIMIT : parsed;
};

const normalizeThreshold = (value: unknown): number => {
  const parsed = parseFloat(String(value));
  if (Number.isNaN(parsed)) return DEFAULT_SEARCH_THRESHOLD;
  return Number((parsed / 100).toFixed(4));
};

const buildGeoPosition = (values: FormValues): Record<string, unknown> | undefined => {
  const geoPosition = {
    origin_longitude: floatOrUndefined(values.origin_longitude),
    origin_latitude: floatOrUndefined(values.origin_latitude),
    longitude_delta: floatOrUndefined(values.longitude_delta),
    latitude_delta: floatOrUndefined(values.latitude_delta),
  };
  const definedEntries = Object.entries(geoPosition).filter(([, value]) => value !== undefined);
  return definedEntries.length > 0 ? Object.fromEntries(definedEntries) : undefined;
};

const buildFilters = (
  values: FormValues,
  candidateOrigin: TCandidateOrigin,
): Record<string, unknown> => {
  const filters: Record<string, unknown> = {
    age__gte: toInt((values.age as unknown[])?.[0]),
    age__lt: toInt((values.age as unknown[])?.[1]),
    // ponytail: apparent_age__lt берёт [0] — паритет с legacy-схемой searchFiltersSchema
    // (там тоже [0]); чинить только вместе с бэкендом
    apparent_age__gte: nanToUndefined(toInt((values.apparent_age as unknown[])?.[0])),
    apparent_age__lt: nanToUndefined(toInt((values.apparent_age as unknown[])?.[0])),
    apparent_gender: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfInt(values.apparent_gender))),
    areas: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.areas))),
    backpack_states: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.backpack_states))),
    cities: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.cities))),
    create_time__gte: toISOString(at0(rawValue(values, 'create_time__gte'))),
    create_time__lt: toISOString(at0(rawValue(values, 'create_time__lt'))),
    end_time__gte: toISOString(at0(rawValue(values, 'end_time__gte'))),
    end_time__lt: toISOString(at0(rawValue(values, 'end_time__lt'))),
    event_id__gte: rawValue(values, 'event_id__gte'),
    event_id__lt: rawValue(values, 'event_id__lt'),
    deepfake: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfInt(values.deepfake))),
    districts: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.districts))),
    emotions: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfInt(values.emotions))),
    event_ids: undefinedIfEmpty(asArrayFromString(values.event_ids)),
    external_ids: undefinedIfEmpty(asArrayFromString(values.external_ids)),
    face_ids: undefinedIfEmpty(asArrayFromString(values.face_ids)),
    gender: toInt((values.gender as unknown[])?.[0]),
    geo_position: buildGeoPosition(values),
    headwear_states: undefinedIfEmpty(
      asArrayWithOptionalNull(asArrayFromString(values.headwear_states)),
    ),
    headwear_apparent_colors: undefinedIfEmpty(
      asArrayWithOptionalNull(asArrayFromString(values.headwear_apparent_colors)),
    ),
    handler_ids: undefinedIfEmpty(asArrayFromString(values.handler_ids)),
    house_numbers: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.house_numbers))),
    liveness: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfInt(values.liveness))),
    list_id: normalizeListId(values.list_id),
    lower_garment_colors: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfOptionValues(values.lower_garment_colors))),
    lower_garment_types: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.lower_garment_types))),
    masks: undefinedIfEmpty(asArrayWithOptionalNull(asArrayOfInt(values.masks))),
    origin: candidateOrigin,
    stream_ids: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.stream_ids))),
    streets: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.streets))),
    sources: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.sources))),
    shoes_apparent_colors: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.shoes_apparent_colors))),
    sleeve_lengths: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.sleeve_lengths))),
    top_similar_object_ids: undefinedIfEmpty(asArrayFromString(values.top_similar_object_ids)),
    top_similar_object_similarity__lt: normalizedPercentOrUndefined(
      values.top_similar_object_similarity__lt,
    ),
    top_similar_object_similarity__gte: normalizedPercentOrUndefined(
      values.top_similar_object_similarity__gte,
    ),
    top_matching_candidates_label: emptyStringToUndefined(values.top_matching_candidates_label),
    tags: undefinedIfEmpty(asArrayOfOptionValues(values.tags)),
    track_ids: undefinedIfEmpty(asArrayWithOptionalNull(asArrayFromString(values.track_ids))),
    upper_clothing_colors: undefinedIfEmpty(
      asArrayWithOptionalNull(asArrayOfOptionValues(values.upper_clothing_colors)),
    ),
    user_data: emptyStringToUndefined(values.user_data),
    attribute_ids: undefinedIfEmpty(asArrayFromString(rawValue(values, 'attribute_ids'))),
  };

  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && !isEmptyValue(value)),
  );
};

export const buildMatchPayload = (
  values: FormValues,
  references: TReference[],
  candidateOrigin: TCandidateOrigin,
): SearchMatchPayload => ({
  references,
  candidates: [{
    filters: buildFilters(values, candidateOrigin),
    order: typeof values.order === 'string' ? values.order : undefined,
    limit: normalizeLimit(values.limit),
    targets: [candidateOrigin === 'events' ? 'event_id' : 'face_id', 'similarity'],
    threshold: normalizeThreshold(values.threshold),
  }],
});
